<script>
    import { onMount, onDestroy, tick } from "svelte";
    import { link } from "svelte-spa-router";
    import Header from "../components/Header.svelte";
    import {
        hostname,
        isAuthenticated,
        showDebug,
        stack,
        currentSession,
    } from "../stores.js";
    import { memcache } from "../downloadstores.js";
    import RangeSlider from "svelte-range-slider-pips";
    import {
        getReconstructionDetailsApi,
        isNumeric,
        now,
        extractTomographyDatasetNameFromPath,
        getTomographyDetailsApi,
        extractDatasetNameFromPath,
        isoDateToDate,
        isoDateToTime,
    } from "../utils.js";
    import { toast } from "@zerodevx/svelte-toast";
    import { toastThemeError, toastThemeSuccess } from "../toasts.js";
    import { downloader, memcacheProgress } from "../downloadstores.js";
    import {
        SLICE_DOWNLOAD_TYPE_PREVIEW,
        USE_DOWNLOAD_CACHE
    } from "../components/Downloader.svelte";
    import Histogram from "../components/Histogram.svelte";
    import { processImage } from "../imageTools.js";
    import ChartHistogram from "svelte-material-icons/ChartHistogram.svelte";
    import NotEqualVariant from "svelte-material-icons/NotEqualVariant.svelte";
    import InvertColors from "svelte-material-icons/InvertColors.svelte";
    import MagnifyPlus from "svelte-material-icons/MagnifyPlus.svelte";
    import MagnifyMinus from "svelte-material-icons/MagnifyMinus.svelte";
    import HandBackRight from "svelte-material-icons/HandBackRight.svelte";
    import RotateLeft from "svelte-material-icons/RotateLeft.svelte";
    import RotateRight from "svelte-material-icons/RotateRight.svelte";
    import CropSquare from "svelte-material-icons/CropSquare.svelte";
    import FilePdfBox from "svelte-material-icons/FilePdfBox.svelte";
    import { canvasSize, zoom, offset, roi, finalFdkSettings } from "../previewstores.js";
    import SlicePicker from "../components/SlicePicker.svelte";
    import ProgressBar from "../components/ProgressBar.svelte";

    export let params = {}; // svelte-spa-router needs this for route parameters

    let currentCacheIndex = 0;
    let currentCacheKey;
    let currentCacheObject;
    let sortedCache = {};

    let cacheSliderValues = [currentCacheIndex];
    let maxSliderValue = currentCacheIndex;

    let minHistogramThreshold;
    let maxHistogramThreshold;

    let sliceOffset = 500;
    let sliceIndex = currentCacheIndex;
    let centershiftSlices = []; // Only here to make SlicePicker happy.
    let beamhardCuppingSlices = []; // Only here to make SlicePicker happy.

    $: $currentSession = params?.session;

    let canvasWidth = $canvasSize;

    let timeout;

    let transparentImg;
    let panImage = false;
    let panActiv = false;
    let container;

    $: $zoom, onCanvasSizeChanged($canvasSize);

    onMount(() => {
        transparentImg = new Image(0, 0);
        transparentImg.src = "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7";
        document.addEventListener("keydown", panKey);
        minHistogramThreshold = 0;
        maxHistogramThreshold = 255;
    });
    onDestroy(() => {
        document.removeEventListener("keydown", panKey);
    });

    // Update current slice (and any reactive slider) from a cache index value (i.e. typically not
    // real slice number, unless *all* reconstructed slices are in the cache).
    function onSelectedSliceSliderValue(value) {
        console.log("ONSELECTED VALUE: ", value);
        currentCacheIndex = value;
        currentCacheKey = Object.keys(sortedCache)[currentCacheIndex];
        currentCacheObject = sortedCache?.[currentCacheKey];
        //console.log(`memcache[${currentCacheIndex}]= slice: ${currentCacheObject?.slice}`);

        clearTimeout(timeout);
        timeout = setTimeout(() => {
            updateImage();
        }, 50);

        // If the main reconstruction slice slider changes, update sliceIndex
        // so that slice picker gets updated
        if (isNumeric(currentCacheObject?.slice)) {
            console.log("ONSELECTED RECONSTRUCTION SLICESLIDER UPDATER: ", currentCacheObject?.slice);
            console.log("ONSELECTED RECONSTRUCTION SLICEPICKER UPDATER: ", sliceIndex);
            sliceIndex = currentCacheObject?.slice - sliceOffset; // TODO: Hard-coded offset. Better to fix sliders to use same range
        }
    }

    // Update current slice (and all reactive sliders) from an actual reconstructed slice.
    // Used by thumbnail picker.
    function onSelectedSlice(value) {
        const newCacheIndex = getSliderValueFromSlice(value, 0);
        if (isNumeric(newCacheIndex) && newCacheIndex != currentCacheIndex) {
            console.log(
                "UPDATING FROM THUMBNAIL CLICK: ",
                newCacheIndex,
                " ==> NEW CACHESLIDERVALUE: ",
                currentCacheIndex
            );
            currentCacheIndex = newCacheIndex;
            cacheSliderValues = [currentCacheIndex];

            currentCacheKey = Object.keys(sortedCache)[currentCacheIndex];
            currentCacheObject = sortedCache?.[currentCacheKey];
            console.log(`memcache[${currentCacheIndex}]= slice: ${currentCacheObject?.slice}`);

            clearTimeout(timeout);
            timeout = setTimeout(() => {
                updateImage();
            }, 100);

            // If the thumbnail picked slice changes, update sliceIndex
            // so that slice picker gets updated
            if (isNumeric(currentCacheObject?.slice)) {
                console.log("ONSELECTED RECONSTRUCTION THUMBNAIL UPDATER: ", currentCacheObject?.slice);
                console.log("ONSELECTED RECONSTRUCTION THUMBNAIL UPDATER: ", sliceIndex);
                sliceIndex = currentCacheObject?.slice - sliceOffset; // TODO: Hard-coded offset. Better to fix sliders to use same range
            }
        }
    }

    $: {
        // If the slice picker slice value changes, update the main reconstruction
        // slice slider.
        const newCacheIndex = getSliderValueFromSlice(sliceIndex, sliceOffset);
        if (isNumeric(newCacheIndex) && newCacheIndex != currentCacheIndex) {
            console.log("UPDATING FROM SLICEPICKER: ", newCacheIndex, " ==> NEW CACHESLIDERVALUE: ", currentCacheIndex);
            currentCacheIndex = newCacheIndex;
            cacheSliderValues = [currentCacheIndex];

            currentCacheKey = Object.keys(sortedCache)[currentCacheIndex];
            currentCacheObject = sortedCache?.[currentCacheKey];
            console.log(`memcache[${currentCacheIndex}]= slice: ${currentCacheObject?.slice}`);

            clearTimeout(timeout);
            timeout = setTimeout(() => {
                updateImage();
            }, 100);
        }
    }

    function getSliderValueFromSlice(slice, sliceOffset) {
        const offsetSlice = slice + (sliceOffset || 0);
        //console.log('SEARCHING CACHE SLIDER VALUE FROM SLICE: ', offsetSlice, '...');
        const newCacheIndex = Object.entries(sortedCache).findIndex((cachedObject) => {
            return cachedObject?.[1]?.slice == offsetSlice;
        });
        //console.log('newCacheIndex=', newCacheIndex);
        return newCacheIndex;
    }

    function getSliceFromSliderValue(sliderValue) {
        let cacheKey = Object.keys(sortedCache)[sliderValue];
        let cacheObject = sortedCache?.[cacheKey];
        return cacheObject?.slice;
    }

    // Change slice when slider stops or a moment after it changed last
    let isSliceSliderMoving = false;
    let sliceSliderTimeout;
    async function onSliceSliderChange(ev) {
        //console.log('SLICE SLIDER CHANGE');
        isSliceSliderMoving = true;
        clearTimeout(sliceSliderTimeout);

        sliceSliderTimeout = setTimeout(() => {
            isSliceSliderMoving = false;
            onSelectedSliceSliderValue(ev.detail.values[0]);
        }, 10);
    }

    async function onSliceSliderStop(ev) {
        //console.log('SLICE SLIDER STOP');
        //console.log('STOPPED CACHE SLIDER=', ev.detail.value)
        isSliceSliderMoving = false;
        clearTimeout(sliceSliderTimeout);

        onSelectedSliceSliderValue(ev.detail.values[0]);
    }

    // TODO: Currently keys are unsorted, so images appear in random order.
    // Make sure they get shown in slice order.
    function resetCacheIndex(cache) {
        if (!cache) return;
        if (Object.keys(cache)?.length == 0) return;
        sortedCache = sortObject(cache);
        let minSliderValue = 0;
        maxSliderValue = Object.keys(cache)?.length - 1;
        currentCacheIndex = 0;
        currentCacheKey = Object.keys(cache)[currentCacheIndex];
        currentCacheObject = cache?.[currentCacheKey];
        console.log("CURRENT CACHE KEY: ", currentCacheKey);
        console.log("MIN CACHE KEY: ", minSliderValue, Object.keys(cache)[minSliderValue]);
        console.log("MAX CACHE KEY: ", maxSliderValue, Object.keys(cache)[maxSliderValue]);
        return true;
    }

    const sortObject = (obj) =>
        Object.keys(obj)
            .sort()
            .reduce((res, key) => ((res[key] = obj[key]), res), {});

    // BEGIN Slicepicker stuff
    $roi = {
        left: 0,
        right: 2047,
        top: 0,
        bot: 2047,
    };
    $finalFdkSettings = {
        min_slice: $roi.top,
        max_slice: $roi.bot,
        angle_offset: 0,
        slice_roi: { ...$roi },
    };

    function setInitialCameraRoi(newRoi) {
        $roi = newRoi;
    }

    function setInitialSliceRoi(newRoi, min_slice, max_slice) {
        // If roi is updated, also update the final settings min_slice and max_slice
        // as well as the pixel roi
        if (newRoi?.top && !isNaN(newRoi.top)) $finalFdkSettings.min_slice = min_slice;
        if (newRoi.bot && !isNaN(newRoi.bot)) {
            $finalFdkSettings.max_slice = max_slice;
        }
        if (newRoi && !isNaN(newRoi.left) && !isNaN(newRoi.right)) {
            $finalFdkSettings.slice_roi = {
                ...newRoi,
            };
        }
    }

    /*function setInitialRoi(newRoi) {
		$roi = newRoi;
		// If roi is updated, also update the final settings min_slice and max_slice 
		// as well as the pixel roi
		if ($roi && $roi.top && !isNaN($roi.top))
			$finalFdkSettings.min_slice = reconstructionDetails.min_slice || $roi.top;
		if ($roi && $roi.bot && !isNaN($roi.bot)) {
			$finalFdkSettings.max_slice = reconstructionDetails.max_slice || $roi.bot;
		}
		if (roi && !isNaN($roi.left) && !isNaN($roi.right)) {
            $finalFdkSettings.slice_roi = {
                top: $roi.left,
                bot: $roi.right,
                left: $roi.left,
                right: $roi.right
            };
		}
	}*/
    // END Slicepicker stuff

    let tomographyDetails;
    async function getTomographyDetails() {
        // Load details about the tomography (e.g. scan pixel size)
        const tomographyDetailsApiResponse = await getTomographyDetailsApi(
            $hostname,
            $isAuthenticated,
            $stack,
            params.session,
            params.tomography
        );
        if (isNumeric(tomographyDetailsApiResponse)) {
            let errorMsg;
            switch (tomographyDetailsApiResponse) {
                case 401:
                    errorMsg = "Not authorised to read";
                    break;
                case 503:
                    errorMsg = "Service unavailable";
                    break;
                default:
                    errorMsg = tomographyDetailsApiResponse;
            }
            let msg = `${now()}<br/>Tomography details API error: ${errorMsg}`;
            toast.push(msg, toastThemeError);

            return; // Not logged in, don't load data
        } else {
            //DEBUG:
            let msg = `${now()}<br/>Fetched tomography OK`;
            //DEBUG:
            toast.push(msg, toastThemeSuccess);
            tomographyDetails = tomographyDetailsApiResponse;
        }

        pixelSizeUm = tomographyDetails?.scan_information?.pixel_size_um;

        const cameraRoi = tomographyDetails?.command?.camera?.roi_px;
        roiImageWidth = Math.abs(cameraRoi.right - cameraRoi.left);
    }

    let reconstructionDetails;
    async function getReconstructionDetails() {
        if (!params?.session || !params?.tomography || !params?.dataset) return;

        const reconstructionDetailsApiResponse = await getReconstructionDetailsApi(
            $hostname,
            $isAuthenticated,
            $stack,
            params.session,
            params.dataset
        );
        if (isNumeric(reconstructionDetailsApiResponse)) {
            let errorMsg;
            switch (reconstructionDetailsApiResponse) {
                case 401:
                    errorMsg = "Not logged in";
                    break;
                case 503:
                    errorMsg = "Service unavailable";
                    break;
                default:
                    errorMsg = reconstructionDetailsApiResponse;
            }
            let msg = `${now()}<br/>Reconstruction details API error: ${errorMsg}`;
            toast.push(msg, toastThemeError);

            return; // Not logged in, don't load data
        }
        const details = reconstructionDetailsApiResponse;
        const name = details?.dataset_id;
        const url = details?.large_image_pattern;
        const minSlice = details?.volume?.min_slice;
        const maxSlice = details?.volume?.max_slice;
        const batchSize = 100;
        const reductionFactor = 64;

        // Get metadata
        const tomography_id = extractTomographyDatasetNameFromPath(url); // TODO: Get this from params instead?
        console.log("GETTING METADATA FOR TOMOGRAPHY ", tomography_id);
        const session_id = params.session;
        console.log(`session_id: ${session_id}`);
        console.log(`tomography_id: ${tomography_id}`);
        console.log(`dataset_id: ${name}`);

        reconstructionDetails = details;
    }

    let useDenormalisation = false;
    function toggleDenormalisation() {
        if (useDenormalisation) useDenormalisation = false;
        else useDenormalisation = true;

        updateImage();
    }

    let useInvert = false;
    let useEdge = false;

    /////////////// BEGIN Image processing

    async function onCanvasSizeChanged(updatedCanvasSize) {
        if (!updatedCanvasSize) return;
        if (!img) {
            img = document.getElementById("img");
        }
        if (!img) return;
        // Update the reactive canvasWidth variable. This will resize the manipulation canvas in the DOM
        canvasWidth = updatedCanvasSize;

        // Update source canvas size
        sourceCanvas.width = canvasWidth * $zoom;
        sourceCanvas.height = canvasWidth * $zoom;

        // Wait for previewManipulationCanvas DOM element to update
        await tick();

        updateImage();

        updateScalebar();
    }

    // Create an off-screen canvas that we can use for internal manipulation.
    // This will be the input to the Histogram component, which will read from
    // this canvas and write to the on-screen reconstructionManipulationCanvas.
    export let sourceCanvas = document.createElement("canvas");
    sourceCanvas.width = canvasWidth * $zoom;
    sourceCanvas.height = canvasWidth * $zoom;
    let sctx = sourceCanvas.getContext("2d");

    let img;

    export let reconstructionManipulationCanvas;

    function updateImage() {
        img = document.getElementById("img");
        if (!img) return;

        //console.log('IMG WIDTH=', img.width);

        if (!sctx) {
            sctx = document.getElementById("sourceCanvas").getContext("2d");
        }

        // Calculate how much the image should be shifted so that its centre
        // is drawn in the centre of the canvas
        const shiftx = (canvasWidth * $zoom - img.width * $zoom) / 2;
        const shifty = (canvasWidth * $zoom - img.height * $zoom) / 2;

        // Draw the base image pixels onto the off-screen sourceCanvas
        sctx.drawImage(img, shiftx, shifty, img.width * $zoom, img.height * $zoom);

        // Get hold of and prepare the on-screen canvas which will be drawn on top of the image
        reconstructionManipulationCanvas = document.getElementById("reconstructionManipulationCanvas");
        let mctx = reconstructionManipulationCanvas.getContext("2d");
        mctx.clearRect(0, 0, img.width * $zoom, img.height * $zoom);
        mctx.drawImage(img, shiftx, shifty, img.width * $zoom, img.height * $zoom);
        // IMG ON MANIPULATION CANVAS');

        // Calculate local and global ranges
        calculateDenormalisedRange();

        let sourceImgData = sctx.getImageData(0, 0, canvasWidth * $zoom, canvasWidth * $zoom);
        const width = sourceImgData.width;
        const height = sourceImgData.height;
        const src = new Uint32Array(sourceImgData.data.buffer);

        //const canvas = reconstructionManipulationCanvas;
        //const outctx = canvas.getContext('2d');
        const outctx = sctx;
        const outImg = outctx.createImageData(width, height);
        const dst = new Uint32Array(outImg.data.buffer);

        if (useDenormalisation) {
            const start = Date.now();
            // Run compressHistogram algorith (denomalise).
            // Read src image data buffer and write result to dst image data buffer
            processCanvas(src, dst, width, height, compressHistogram);
            const stop = Date.now();
            console.log("COMPRESSHISTOGRAM TOOK ", stop - start, " ms");

            // Write the resulting image data buffer to the output canvas.
            // In this case, the source canvas, so that Histogram can use it.
            outctx.putImageData(outImg, 0, 0);

            // In case we don't have the Histogram component enabled, then we have
            // to write the image data buffer to the manipulation canvas ourselves.
            if (!isHistogramVisible) {
                mctx.putImageData(outImg, 0, 0);

                // In case the source canvas has been updated, we need to refresh the image data
                // so that the processImage can take it as input when processing for manipulation canvas 
                sourceImgData = sctx.getImageData(0, 0, canvasWidth * $zoom, canvasWidth * $zoom);
            }
        }

        sourceCanvas = sourceCanvas;
        reconstructionManipulationCanvas.style.translate = `${$offset.x}px ${$offset.y}px`;

        processImage(sourceImgData, reconstructionManipulationCanvas, minHistogramThreshold, maxHistogramThreshold);

        // DEBUG: Cross hair and corner
        /*
        const mwidth = reconstructionManipulationCanvas.width;
        const mheight = reconstructionManipulationCanvas.height;
        mctx.beginPath();
        mctx.moveTo(mwidth / 2 - 50, mheight / 2);
        mctx.lineTo(mwidth / 2 + 50, mheight / 2);
        mctx.moveTo(mwidth / 2, mheight / 2 - 50);
        mctx.lineTo(mwidth / 2, mheight / 2 + 50);
        mctx.strokeStyle = "yellow";
        mctx.stroke();
        // DEBUG: Draw lower right corner marker
        mctx.beginPath();
        mctx.moveTo(mwidth - 50, mheight);
        mctx.lineTo(mwidth, mheight);
        mctx.moveTo(mwidth, mheight - 50);
        mctx.lineTo(mwidth, mheight);
        mctx.strokeStyle = "yellow";
        mctx.stroke();
        */
        // Update histogram if visible
        if (isHistogramVisible) {
            onShowHistogram();
        }
    }

    let white; // DEBUG
    let black; // DEBUG
    let localMin;
    let localMax;
    let globalMin;
    let globalMax;
    // Fetch local and global min/max greyscale intensities for use by denormalisation calculations
    function calculateDenormalisedRange() {
        localMin = reconstructionDetails?.slice_min_in_float32?.[currentCacheObject?.slice - sliceOffset];
        localMax = reconstructionDetails?.slice_max_in_float32?.[currentCacheObject?.slice - sliceOffset];
        const localRange = localMax - localMin;

        globalMin = reconstructionDetails?.global_min_in_float32;
        globalMax = reconstructionDetails?.global_max_in_float32;
        const globalRange = globalMax - globalMin;

        const localBlack = (0 * localRange) / 255 + localMin;
        const localWhite = (255 * localRange) / 255 + localMin;

        const globalBlack = ((localBlack - globalMin) / globalRange) * 255;
        const globalWhite = ((localWhite - globalMin) / globalRange) * 255;

        black = globalBlack;
        white = globalWhite;
    }

    // Wrapper function for any type of manipulation we might want to do. Algorithm supplied as func.
    function processCanvas(src, dst, width, height, func) {
        func(src, dst, {
            localMin: localMin,
            localMax: localMax,
            globalMin: globalMin,
            globalMax: globalMax,
        });
    }

    // Compress histogram into the global range
    // In effect, this decreases contrast, but brings all images
    // to the same range.
    function compressHistogram(src, dst, options) {
        console.log(`DENORMALISING SRC IMAGE... [${black}, ${white}]`);
        let brightness = 0;
        const localMin = options.localMin;
        const localMax = options.localMax;
        const globalMin = options.globalMin;
        const globalMax = options.globalMax;
        const localRange = localMax - localMin;
        const globalRange = globalMax - globalMin;

        /*
		let avgGray = 0;
		for (let i = 0; i < dst.length; i++) {
			let r = src[i] & 0xFF;
			let g = (src[i] >> 8) & 0xFF;
			let b = (src[i] >> 16) & 0xFF;
			avgGray += (r + g + b) / 3;
		}
		avgGray /= dst.length;
        */

        for (let i = 0; i < dst.length; i++) {
            let r = src[i] & 0xff;
            let g = (src[i] >> 8) & 0xff;
            let b = (src[i] >> 16) & 0xff;

            // Contrast
            r = (r * localRange) / 255 + localMin;
            g = (g * localRange) / 255 + localMin;
            b = (b * localRange) / 255 + localMin;

            r = ((r - globalMin) / globalRange) * 255;
            g = ((g - globalMin) / globalRange) * 255;
            b = ((b - globalMin) / globalRange) * 255;

            if (r > 255) r = 255;
            else if (r < 0) r = 0;
            if (g > 255) g = 255;
            else if (g < 0) g = 0;
            if (b > 255) b = 255;
            else if (b < 0) b = 0;

            dst[i] = (src[i] & 0xff000000) | (b << 16) | (g << 8) | r;
            /* DEBUG:
            let diff = (dst[i] & 0xFF) - (src[i] & 0xFF);
			if (i > 100 * 100 && i < 100 * 101) {
				console.log(`DST-SRC DIFF=${diff} (${dst[i] & 0xFF} <-- ${src[i] & 0xFF})`);
			}
            */
        }
    }
    /////////////// END Image processing

    let rotating = false;
    let interval;
    function startRotateImageLeft() {
        rotating = true;
        interval = setInterval(() => {
            if (rotating) {
                if ($finalFdkSettings.angle_offset < 360)
                    $finalFdkSettings.angle_offset = $finalFdkSettings.angle_offset + 1;
            }
        }, 50);
    }

    function startRotateImageRight() {
        rotating = true;
        interval = setInterval(() => {
            if (rotating) {
                if ($finalFdkSettings.angle_offset > -360)
                    $finalFdkSettings.angle_offset = $finalFdkSettings.angle_offset - 1;
            }
        }, 50);
    }

    function stopRotateImage() {
        rotating = false;
        clearInterval(interval);
    }

    function activatePan() {
        panActiv = !panActiv;
        if (panActiv) {
            container.style.cursor = "grab";
        } else {
            container.style.cursor = "default";
        }
    }

    function panStart() {
        panImage = true;
    }

    let mouseX;
    let mouseY;
    function panMove(event) {
        if (panImage && (event.altKey || panActiv)) {
            container.style.cursor = "grabbing";
            $offset.x += event.movementX;
            $offset.y += event.movementY;
            reconstructionManipulationCanvas.style.translate = `${$offset.x}px ${$offset.y}px`;
        } else {
            mouseX = event.offsetX;
            mouseY = event.offsetY;
        }
    }

    function panEnd(e) {
        if (panActiv) {
            container.style.cursor = "grab";
        } else {
            container.style.cursor = "default";
        }
        panImage = false;
    }

    function panKey(event) {
        if (event.keyCode === 72) activatePan();
    }

    function zoomOut() {
        if ($zoom === 1) {
            return;
        }
        const bigBox = canvasWidth * $zoom;
        const smallBox = canvasWidth * ($zoom - 1);
        const scale = smallBox / bigBox;
        const newcenterValue = (bigBox - smallBox) / 2;

        $offset.x = $offset.x + newcenterValue;
        $offset.y = $offset.y + newcenterValue;
        reconstructionManipulationCanvas.style.translate = `${$offset.x}px ${$offset.y}px`;
        $zoom = $zoom - 1;
    }

    let zoomclickX;
    let zoomclickY;
    function onZoomClick(event) {
        if (reconstructionManipulationCanvas.width > 4096 || reconstructionManipulationCanvas.height > 4096) {
            return;
        }

        const smallBox = canvasWidth * $zoom;
        const bigBox = canvasWidth * ($zoom + 1);
        const scale = bigBox / smallBox;
        const newcenterValue = (smallBox - bigBox) / 2;

        if (event) {
            /*const rect = event.target.getBoundingClientRect();
            const pointX = (event.clientX - rect.left) - canvasWidth / 2;
            const pointY = (event.clientY - rect.top) - canvasWidth / 2;
            let testX = ($offset.x - pointX  ) * scale;
            let testY = ($offset.y - pointY  ) * scale;

            //const centerValue = (((img.width * ($zoom + 1)) - (canvasWidth * $zoom)) / 2 );
            if ($zoom === 1){
                testX = testX + newcenterValue ;
                testY = testY + newcenterValue;
            }
            $offset.x = testX;
            $offset.y = testY;*/

            // User test: centering on zoom click (vs keep click point stable)
            zoomclickX = event.offsetX;
            zoomclickY = event.offsetY;

            const newclickX = (event.offsetX / smallBox) * bigBox;
            const newclickY = (event.offsetY / smallBox) * bigBox;

            $offset.x = canvasWidth / 2 - newclickX;
            $offset.y = canvasWidth / 2 - newclickY;
        } else {
            $offset.x = $offset.x + newcenterValue;
            $offset.y = $offset.y + newcenterValue;
        }

        reconstructionManipulationCanvas.style.translate = `${$offset.x}px ${$offset.y}px`;
        $zoom = $zoom + 1;
    }

    function resetImage() {
        $offset.x = 0;
        $offset.y = 0;
        $zoom = 1;
        reconstructionManipulationCanvas.style.translate = `${$offset.x}px ${$offset.y}px`;
    }

    // BEGIN Histogram
    let isHistogramVisible = false;

    async function onToggleHistogram() {
        if (!isHistogramVisible) {
            await onShowHistogram();
        } else {
            await onHideHistogram();
        }
    }

    async function onShowHistogram() {
        isHistogramVisible = true;
    }

    async function onHideHistogram() {
        isHistogramVisible = false;
        await tick();
    }
    // END Histogram

    let mounted;
    let initialised = false;
    $: {
        if ($isAuthenticated && !initialised) {
            initialised = true;
            console.log("~~~~~~~~~~~~~~~INITIALISING RECONSTRUCTION BECAUSE AUTHENTICATED~~~~~~~~~~~~~~~~");
            initialiseWithData();
        }
    }

    async function initialiseWithData() {
        await getTomographyDetails();
        console.log("TOMOGRAPHY", tomographyDetails);

        await getReconstructionDetails();
        const name = reconstructionDetails?.dataset_id;
        const url = reconstructionDetails?.large_image_pattern;
        const minSlice = reconstructionDetails?.volume?.min_slice;
        const maxSlice = reconstructionDetails?.volume?.max_slice;
        const batchSize = 100;
        const reductionFactor = 64;
        console.log("DETAILS = ", reconstructionDetails);

        const cameraRoi = tomographyDetails?.command?.camera?.roi_px;
        roiImageWidth = Math.abs(cameraRoi.right - cameraRoi.left + 1);
        console.log("CAMERA ROI IS: ", cameraRoi);

        const volumeRoi = reconstructionDetails?.volume?.slice_roi;
        const min_slice = reconstructionDetails?.volume?.min_slice;
        const max_slice = reconstructionDetails?.volume?.max_slice;
        console.log("SLICE ROI IS: ", volumeRoi);

        // For slicepicker.
        // Rescale to new roi bounds counting from zero (disregard cropped)
        let newRoi = {
            left: cameraRoi.left,
            right: cameraRoi.right,
            top: minSlice,
            bot: maxSlice,
        };
        setInitialCameraRoi(newRoi);
        sliceOffset = minSlice;

        // If we have maximumVolume info, use this instead
        if (volumeRoi) {
            newRoi = { ...volumeRoi };
            setInitialSliceRoi(newRoi, min_slice, max_slice);
            roiImageWidth = Math.abs(volumeRoi.right - volumeRoi.left + 1);
        }

        // Download thumbnails or previews into cache. We don't expect the user to do other things while the
        // dataset loads, so use a large batch size (number of open connections)
        if (reconstructionDetails && !isNumeric(reconstructionDetails)) {
            console.log("DOWNLOADING RECONSTRUCTED IMAGES!");
            await $downloader?.onDownloadImg(
                name,
                url,
                1,
                minSlice,
                maxSlice,
                SLICE_DOWNLOAD_TYPE_PREVIEW,
                USE_DOWNLOAD_CACHE,
                batchSize,
                reductionFactor
            );
            console.log(`DONE DOWNLOADING RECONSTRUCTION IMAGES!`);
            mounted = resetCacheIndex($memcache);
        }

        await tick();

        clearTimeout(timeout);
        timeout = setTimeout(() => {
            updateImage();
            updateScalebar();
        }, 100);
    }

    ////////// BEGIN Scalebar //////////
    let scalelength = 50;
    let scalemicrons = 1;
    let pixelSizeUm = 1; // Fetch from tomo details
    let roiImageWidth = 2048; // Get from tomo details

    function updateScalebar() {
        console.log(
            ` ---- UPDATING SCALEBAR pixelSizeUm=${pixelSizeUm}, canvasWidth=${canvasWidth}, roiImageWidth=${roiImageWidth}, $zoom=${$zoom}`
        );

        scalelength = 50 / $zoom;

        // Try to get an even scalemicrons value (e.g. 1, 10, 100, 1000) and adapt the
        // scalebar length accordingly, but keep scalelength around 100 pixels
        scalemicrons = Math.round(pixelSizeUm * ((roiImageWidth / canvasWidth) * $zoom) * scalelength);

        // Entire image with is px * iw microns wide
        // On-screen canvas scale is cw / iw, so 100 pixels would mean 100 * cw / iw pixels on-screen
        // 1 pixel on-screen is px * iw / cw microns
        // mu = px * iw / cw * sl
        console.log(` ---- INTERMEDIATE UPDATED SCALEBAR scalemicrons=${scalemicrons}, scalelength=${scalelength}`);

        if (scalemicrons <= 1) {
            scalemicrons = 1;
        } else if (scalemicrons > 1 && scalemicrons <= 10) {
            scalemicrons = 10;
        } else if (scalemicrons > 10 && scalemicrons <= 100) {
            scalemicrons = 100;
        } else if (scalemicrons > 100 && scalemicrons <= 200) {
            scalemicrons = 200;
        } else if (scalemicrons > 200 && scalemicrons <= 500) {
            scalemicrons = 500;
        } else if (scalemicrons > 500 && scalemicrons <= 1000) {
            scalemicrons = 1000;
        } else if (scalemicrons > 1000 && scalemicrons <= 2000) {
            scalemicrons = 2000;
        } else if (scalemicrons > 2000 && scalemicrons <= 5000) {
            scalemicrons = 5000;
        } else if (scalemicrons > 5000) {
            scalemicrons = 10000;
        }

        // sl = mu / px / iw * cw
        scalelength = Math.round(((scalemicrons / pixelSizeUm) * (canvasWidth * $zoom)) / roiImageWidth);

        console.log(` ---- UPDATED FINAL SCALEBAR scalemicrons=${scalemicrons}, scalelength=${scalelength}`);
    }

    function formatScalemicrons(microns) {
        let number = microns >= 1000 ? microns / 1000 : microns;
        let unit = microns >= 1000 ? "mm" : "μm";
        return Math.round(number) + " " + unit;
    }
    ////////// END Scalebar //////////
</script>

<Header />

<main>
    <nav>
        <a href="/" use:link>Home</a>
        &nbsp;
        <a href="/details/{params.session}" use:link>Details</a>
    </nav>
    <div>
        {params.session}/{params.tomography}&nbsp;({$isAuthenticated ? "Authenticated" : "Not authenticated"})
        {#if reconstructionDetails?.report}
            <div style="display: inline-block; vertical-align: middle; margin-left: 16px;">
                <div style="display: flex;">
                    <FilePdfBox color="currentColor" size="1.5em" viewBox="0 2 24 24" />&nbsp; 
                    <a href="{reconstructionDetails?.report}" target="_blank">Reconstruction report</a>
                </div>
            </div>
        {/if}
    </div>
    
    <reconstruction-pane>
        <slice-selection-pane>
            <div><h3>Select slice...</h3></div>
            <br />
            {#if mounted}
                <SlicePicker
                    tomography={tomographyDetails}
                    currentPreviewType={"CENTERSHIFT"}
                    bind:sliceIndex
                    {centershiftSlices}
                    {beamhardCuppingSlices}
                    step={64}
                    crop={true} />
            {/if}
            &nbsp;
            <br />
            &nbsp;
            <br />
            &nbsp;
            <br />
            {#if $showDebug}
                <div style="display:flex; flex-direction: column;">
                    <annotation>canvasWidth: {canvasWidth}</annotation>
                    <annotation>zoom: {$zoom}</annotation>
                    <annotation>
                        offset: {Math.round($offset?.x)}
                        {Math.round($offset?.y)}
                    </annotation>
                    <annotation>angle: {$finalFdkSettings?.angle_offset}</annotation>
                    <annotation>currentCacheIndex: {currentCacheIndex}</annotation>
                    <annotation>currentCacheObject: {currentCacheObject?.slice}</annotation>
                    <annotation>localMin: {localMin}</annotation>
                    <annotation>localMax: {localMax}</annotation>
                    <annotation>globalMin: {reconstructionDetails?.global_min_in_float32}</annotation>
                    <annotation>globalMax: {reconstructionDetails?.global_max_in_float32}</annotation>
                    <annotation>
                        sliceMin: {reconstructionDetails?.slice_min_in_float32?.[currentCacheObject?.slice]}
                    </annotation>
                    <annotation>
                        sliceMax: {reconstructionDetails?.slice_max_in_float32?.[currentCacheObject?.slice]}
                    </annotation>
                    <annotation>black: {black} <!-- DEBUG --></annotation>
                    <annotation>white: {white} <!-- DEBUG --></annotation>
                    <annotation>pixelSizeUm: {pixelSizeUm}</annotation>
                    <annotation>sliceOffset: {sliceOffset}</annotation>
                    <annotation>minSlice: {reconstructionDetails?.min_slice}</annotation>
                    <annotation>cameraRoiImageHeight: {$roi?.bot - $roi?.top + 1}</annotation>
                    <annotation>cameraRoiImageWidth: {$roi?.right - $roi?.left + 1}</annotation>
                    <annotation>roiImageWidth: {roiImageWidth}</annotation>
                    <annotation>manipulationCanvasWidth: {reconstructionManipulationCanvas?.width}</annotation>
                    <annotation>zoomclick: {zoomclickX} {zoomclickY}</annotation>
                    <annotation>mouse: {mouseX} {mouseY}</annotation>
                    <annotation>report: <a href="{reconstructionDetails?.report}" target="_blank">PDF</a></annotation>
                </div>
            {/if}
        </slice-selection-pane>
        <slice-browser>
            <div style="text-align: left; width: 100%">
                <h2>Reconstructed slices</h2>
                <p>
                    Dataset: {extractDatasetNameFromPath(reconstructionDetails?.dataset_id, sliceIndex)} &emsp; Created:
                    {isoDateToDate(reconstructionDetails?.start_time)}
                    {isoDateToTime(reconstructionDetails?.start_time)} &emsp; Size: {reconstructionDetails?.size_GB
                        ? Math.round(reconstructionDetails?.size_GB) + " GB"
                        : ""}
                    <br />
                    δ: {reconstructionDetails?.phase_retrieval_settings?.phase_retrieval?.delta
                        ? reconstructionDetails?.phase_retrieval_settings?.phase_retrieval?.delta?.toExponential()
                        : ""} &emsp; μ: {reconstructionDetails?.phase_retrieval_settings?.phase_retrieval?.mu
                            ? reconstructionDetails?.phase_retrieval_settings?.phase_retrieval?.mu?.toPrecision(3)
                            : ""}
                    <br />
                    Centershift: {reconstructionDetails?.reconstruction_settings?.centershift_object_mm
                        ? (reconstructionDetails?.reconstruction_settings?.centershift_object_mm * 1000).toFixed(2)
                        : ""} μm &emsp; {#if $showDebug}Tilt: {reconstructionDetails?.reconstruction_settings?.tilt_deg
                            ? reconstructionDetails?.reconstruction_settings?.tilt_deg?.toPrecision(3)
                            : 0} ° {/if}
                    <br />
                    Beam hardening: {reconstructionDetails?.reconstruction_settings?.beamhard_cupping
                        ? reconstructionDetails?.reconstruction_settings?.beamhard_cupping
                        : 0}
                    <br />
                    Slice: {currentCacheObject?.slice} (full tomography volume) &emsp; {sliceIndex} (reconstructed ROI)
                </p>
            </div>
            {#if mounted}
                <!-- svelte-ignore a11y-no-static-element-interactions -->
                <reconstruction-image-box
                    bind:this={container}
                    on:dblclick={(event) => onZoomClick(event)}
                    on:mousemove={panMove}
                    on:mousedown={(e) => panStart(e)}
                    on:mouseup={(e) => panEnd(e)}
                    on:mouseleave={() => (panImage = false)}
                    style="--canvasWidth:{canvasWidth}px">
                    <img
                        id="img"
                        src={currentCacheObject?.blob}
                        alt={currentCacheObject?.slice}
                        class="slice {useInvert ? 'invert' : ''} {isNumeric($finalFdkSettings?.angle_offset)
                            ? 'rotate'
                            : ''}"
                        style="--angle:{-$finalFdkSettings.angle_offset}deg; visibility: hidden;" />
                    <!-- svelte-ignore a11y-mouse-events-have-key-events -->
                    <canvas
                        id="reconstructionManipulationCanvas"
                        class:invert={useInvert}
                        class:edge={useEdge}
                        width={canvasWidth * $zoom}
                        height={canvasWidth * $zoom}
                        class={isNumeric($finalFdkSettings?.angle_offset) ? "rotate" : ""}
                        style="--angle:{-$finalFdkSettings.angle_offset}deg;" />
                    <scalebar>
                        <div class="scaleline {useInvert ? 'invert' : ''}" style="width: {scalelength}px;">
                            {formatScalemicrons(scalemicrons)}
                        </div>
                    </scalebar>
                    <svg xmlns="http://www.w3.org/2000/svg" version="1.1" width="0%" height="0%">
                        <filter id="edgedetect">
                            <feConvolveMatrix
                                order="5 5"
                                preserveAlpha="true"
                                kernelMatrix=" -0 -0 -1 -0 -0 
                                                                                            -0 -1 -2 -1 -0 
                                                                                            -1 -2 16 -2 -1 
                                                                                            -0 -1 -2 -1 -0
                                                                                            -0 -0 -1 -0 -0" />
                        </filter>
                        <filter id="prewitt">
                            <feConvolveMatrix
                                order="3 3"
                                preserveAlpha="true"
                                kernelMatrix=" -0  1  2
                                                                                              -1 -0 -1
                                                                                              -2 -1 -0 "
                                divisor="2" />
                        </filter>
                    </svg>
                    <svg version="1.1" width="0" height="0" style="position: absolute;">
                        <svg version="1.1" width="0" height="0" style="position: absolute;">
                            <filter id="Laplacian">
                                <feConvolveMatrix
                                    in="SourceGraphic"
                                    preserveAlpha="true"
                                    result="C1"
                                    kernelMatrix="-1,-1,-1,-1,8,-1,-1,-1,-1" />
                                <feColorMatrix type="saturate" values="0" />
                            </filter>
                        </svg>
                        <filter id="Sobel">
                            <feConvolveMatrix
                                in="SourceGraphic"
                                preserveAlpha="true"
                                order="3"
                                kernelMatrix="-1,-2,-1,0,0,0,1,2,1"
                                result="V1" />
                            <feConvolveMatrix
                                in="SourceGraphic"
                                preserveAlpha="true"
                                order="3"
                                kernelMatrix="1,2,1,0,0,0,-1,-2,-1"
                                result="V2" />
                            <feConvolveMatrix
                                in="SourceGraphic"
                                preserveAlpha="true"
                                order="3"
                                kernelMatrix="-1,0,1,-2,0,2,-1,0,1"
                                result="H1" />
                            <feConvolveMatrix
                                in="SourceGraphic"
                                preserveAlpha="true"
                                order="3"
                                kernelMatrix="1,0,-1,2,0,-2,1,0,-1"
                                result="H2" />
                            <feComposite operator="arithmetic" in="V1" in2="V2" k2="1" k3="1" result="V" />
                            <feComposite operator="arithmetic" in="H1" in2="H2" k2="1" k3="1" result="H" />
                            <feBlend mode="lighten" in="H" in2="V" />
                            <feColorMatrix type="saturate" values="0" />
                        </filter>
                    </svg>
                    <svg version="1.1" width="0" height="0" style="position: absolute;">
                        <filter id="Prewitt">
                            <feConvolveMatrix
                                in="SourceGraphic"
                                preserveAlpha="true"
                                order="3"
                                kernelMatrix="-1,-1,-1,0,0,0,1,1,1"
                                result="V1" />
                            <feConvolveMatrix
                                in="SourceGraphic"
                                preserveAlpha="true"
                                order="3"
                                kernelMatrix="1,1,1,0,0,0,-1,-1,-1"
                                result="V2" />
                            <feConvolveMatrix
                                in="SourceGraphic"
                                preserveAlpha="true"
                                order="3"
                                kernelMatrix="-1,0,1,-1,0,1,-1,0,1"
                                result="H1" />
                            <feConvolveMatrix
                                in="SourceGraphic"
                                preserveAlpha="true"
                                order="3"
                                kernelMatrix="1,0,-1,1,0,-1,1,0,-1"
                                result="H2" />
                            <feComposite operator="arithmetic" in="V1" in2="V2" k2="1" k3="1" result="V" />
                            <feComposite operator="arithmetic" in="H1" in2="H2" k2="1" k3="1" result="H" />
                            <feBlend mode="lighten" in="H" in2="V" />
                            <feColorMatrix type="saturate" values="0" />
                        </filter>
                    </svg>
                    <svg version="1.1" width="0" height="0" style="position: absolute;">
                        <filter id="Roberts">
                            <feConvolveMatrix
                                in="SourceGraphic"
                                preserveAlpha="true"
                                order="3"
                                kernelMatrix="-1,0,0,0,0,0,0,0,1"
                                result="V1" />
                            <feConvolveMatrix
                                in="SourceGraphic"
                                preserveAlpha="true"
                                order="3"
                                kernelMatrix="1,0,0,0,0,0,0,0,-1"
                                result="V2" />
                            <feConvolveMatrix
                                in="SourceGraphic"
                                preserveAlpha="true"
                                order="3"
                                kernelMatrix="0,0,1,0,0,0,-1,0,0"
                                result="H1" />
                            <feConvolveMatrix
                                in="SourceGraphic"
                                preserveAlpha="true"
                                order="3"
                                kernelMatrix="0,0,-1,0,0,0,1,0,0"
                                result="H2" />
                            <feComposite operator="arithmetic" in="V1" in2="V2" k2="1" k3="1" result="V" />
                            <feComposite operator="arithmetic" in="H1" in2="H2" k2="1" k3="1" result="H" />
                            <feBlend mode="lighten" in="H" in2="V" />
                            <feColorMatrix type="saturate" values="0" />
                        </filter>
                    </svg>
                </reconstruction-image-box>
                <RangeSlider
                    id="recSliceSlider"
                    values={cacheSliderValues}
                    max={maxSliderValue}
                    step
                    pips
                    float
                    first="label"
                    last="label"
                    formatter={(v) => {
                        return getSliceFromSliderValue(v);
                    }}
                    springValues={{stiffness: 1, damping:1}}
                    on:change={onSliceSliderChange}
                    on:stop={onSliceSliderStop} />
                <reconstruction-image-toolbar>
                    <toolbar-button>
                        <button class:active={useDenormalisation} on:click={toggleDenormalisation}>
                            <NotEqualVariant color="currentColor" size="1.5em" />Denormalise
                        </button>
                    </toolbar-button>
                    <toolbar-button>
                        <button class:active={useInvert} on:click={() => (useInvert = !useInvert)}>
                            <InvertColors color="currentColor" size="1.5em" />Invert
                        </button>
                    </toolbar-button>
                    <!-- DISABLED FOR NOW:
                    <toolbar-button>
                        <button class:active={useEdge} on:click={()=> useEdge = !useEdge}><ArrowCollapseHorizontal color="currentColor" size="1.5em"/>Edge</button>
                    </toolbar-button>
                    -->
                    <toolbar-button>
                        <button class:active={isHistogramVisible} on:click={onToggleHistogram}>
                            <ChartHistogram color="currentColor" size="1.5em" />Histogram
                        </button>
                    </toolbar-button>
                    <toolbar-button>
                        <button on:click={resetImage}>
                            <CropSquare color="currentColor" size="1.5em" />
                        </button>
                    </toolbar-button>
                    <toolbar-button>
                        <button on:mousedown={startRotateImageLeft} on:mouseup={stopRotateImage}>
                            <RotateLeft color="currentColor" size="1.5em" />
                        </button>
                    </toolbar-button>
                    <toolbar-button>
                        <button on:mousedown={startRotateImageRight} on:mouseup={stopRotateImage}>
                            <RotateRight color="currentColor" size="1.5em" />
                        </button>
                    </toolbar-button>
                    <toolbar-button>
                        <button on:click={() => onZoomClick()}>
                            <MagnifyPlus color="currentColor" size="1.5em" />
                        </button>
                    </toolbar-button>
                    <toolbar-button>
                        <button on:click={() => zoomOut()}>
                            <MagnifyMinus color="currentColor" size="1.5em" />
                        </button>
                    </toolbar-button>
                    <toolbar-button>
                        <button class:active={panActiv} on:click={activatePan}>
                            <HandBackRight color="currentColor" size="1.5em" />
                        </button>
                    </toolbar-button>
                </reconstruction-image-toolbar>
                <div style="display:flex; text-align: left; margin-bottom: 32px;">
                    {#if isHistogramVisible}
                        <Histogram
                            {canvasWidth}
                            zoom={$zoom}
                            {sourceCanvas}
                            manipulationCanvas={reconstructionManipulationCanvas}
                            bind:minHistogramThreshold
                            bind:maxHistogramThreshold />
                    {/if}
                </div>
            {:else}
                <div style="text-align: center;">
                    <p>
                        Loading... {Object.keys($memcache).length} / {$memcacheProgress?.expectedCount}
                    </p>
                    <ProgressBar percent={Object.keys($memcache)?.length / $memcacheProgress?.expectedCount || 0} />
                </div>
            {/if}
        </slice-browser>
    </reconstruction-pane>
    <thumbnails-grid>
        {#each Object.entries(sortedCache) as [name, data]}
            <!--DEBUG:{#if data.slice % 100 == 0}-->
            <div
                style="width: fit-content; box-sizing: border-box;"
                class:current={data?.slice == currentCacheObject?.slice}
                on:click={onSelectedSlice(data?.slice)}>
                <img class="thumbnail {useInvert ? 'invert' : ''}" src={data?.blob} alt={name} title={data?.slice} />
            </div>
            <!--{/if}-->
        {/each}
    </thumbnails-grid>
    <!--div style="display: block; width: 80%; margin: 16px;">Details: {JSON.stringify(reconstructionDetails)}</div-->
</main>

<style>
    main {
        text-align: left;
        padding: 0;
        margin: 0 auto;
        display: block;
    }

    @media (min-width: 640px) {
        main {
            max-width: none;
        }
    }
    reconstruction-pane {
        display: flex;
        flex-wrap: wrap;
        text-align: left;
    }

    slice-selection-pane {
        border: 1px solid var(--var-theme-base-color);
        background-color: var(--var-theme-base-background-color);
        padding: 8px;
        margin: 8px;
        display: flex;
        flex-wrap: wrap;
        flex-direction: column;
    }

    slice-browser {
        display: flex;
        flex-direction: column;
        flex-wrap: wrap;
        justify-content: flex-start;
        align-items: center;
        align-content: flex-start;
        gap: 8px 8px;
        margin: auto;
        background-color: var(--var-theme-base-background-color);
        padding: 8px 24px 24px 24px;
        margin: 8px;
        border: 1px solid var(--var-theme-base-color);
    }

    reconstruction-image-box {
        display: inline-flex;
        height: var(--canvasWidth);
        width: var(--canvasWidth);
        max-height: 2048px;
        max-width: 2048px;
        margin-bottom: 20px;
        overflow: hidden;
        background-color: black;
        position: relative;
    }

    .slice {
        height: var(--canvasWidth);
    }
    :global(#recSliceSlider) {
        display: block;
        width: 100%;
        margin: 0;
        margin-left: auto;
        margin-right: auto;
        margin-bottom: 32px;
    }

    :global(#recSliceSlider .pip.last, #recSliceSlider .pip.first) {
        height: 0.75em;
    }
    :global(#recSliceSlider .rangePips .pip.first .pipVal) {
        padding-left: 1.5em;
    }
    :global(#recSliceSlider .rangePips .pip.last .pipVal) {
        padding-right: 1.5em;
    }

    .slice.invert {
        filter: invert(100%);
    }

    .rotate {
        transform: rotate(var(--angle));
    }

    #reconstructionManipulationCanvas {
        position: absolute;
        user-select: none;
        translate: -100%;
    }

    #reconstructionManipulationCanvas.invert {
        filter: invert(100%);
    }

    #reconstructionManipulationCanvas.edge {
        filter: url(#Sobel);
    }

    #reconstructionManipulationCanvas.invert.edge {
        filter: url(#Sobel) invert(100%);
    }

    scalebar {
        position: absolute;
        bottom: 0;
        right: 24px;
        color: yellow;
    }

    .scaleline {
        border-top: 4px solid;
        padding: 8px;
        margin-bottom: 4px;
        text-align: center;
    }
    scalebar .invert {
        color: blue;
    }
    reconstruction-image-toolbar {
        display: flex;
        flex-direction: row;
        gap: 16px;
        float: right;
    }

    toolbar-button .active {
        background-color: var(--var-theme-hover-background-color);
    }
    toolbar-button button {
        display: flex;
        align-items: center;
        padding-bottom: 6px; /* Allow for 1.5em icon */
        padding-top: 6px; /* Allow for 1.5em icon */
    }

    thumbnails-grid {
        display: flex;
        flex-direction: row;
        flex-wrap: wrap;
        justify-content: flex-start;
        align-items: flex-start;
        align-content: flex-start;
        gap: 1px 1px;
        width: 100%;
        margin: auto;
        line-height: 0;
    }

    .current {
        outline: 2px green solid;
        outline-offset: -1px;
    }

    .thumbnail {
        display: inline-block;
        height: 64px;
    }

    .thumbnail.invert {
        filter: invert(100%);
    }
</style>
